import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useNotificationStore } from '@/store/notificationStore';
import { useThemeStore } from '@/store/themeStore';
import NotificationPanel from './NotificationPanel';
import { authApi, notificationApi } from '@/services/api';
import Avatar from '@/components/ui/Avatar';
import {
  LayoutDashboard,
  CalendarDays,
  FilePlus,
  Inbox,
  Users,
  Building2,
  UserCog,
  Settings,
  FileText,
  ShieldCheck,
  LogOut,
  Bell,
  Menu,
  X,
  User as UserIcon,
  Flag,
  Coffee,
  Sun,
  Moon,
  Monitor,
} from 'lucide-react';

export default function AppShell() {
  const { user, clearAuth } = useAuthStore();
  const { unreadCount, togglePanel, setNotifications } = useNotificationStore();
  const { theme, setTheme } = useThemeStore();
  const cycleTheme = () => {
    const next = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
    setTheme(next);
  };
  const ThemeIcon = theme === 'dark' ? Moon : theme === 'system' ? Monitor : Sun;
  const navigate = useNavigate();

  // Load unread count on mount so bell shows correct badge immediately
  useEffect(() => {
    notificationApi.getAll({ limit: 50 }).then((res) => {
      setNotifications(res.data.data, res.data.unreadCount);
    }).catch(() => { });
  }, []);
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  if (!user) return null;

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch (e) {
      console.error(e);
    } finally {
      clearAuth();
      navigate('/login');
    }
  };

  const role = user.role;
  const isManagerByRole = ['MANAGER', 'SENIOR_MANAGER', 'MD'].includes(role);
  const isManagerPlus = isManagerByRole || !!user.hasDirectReports;
  const isMD = role === 'MD';
  const isOrgAdmin = role === 'ORG_ADMIN';
  const isHR = role === 'HR_MANAGER';
  const isManagerNotMD = isManagerPlus && !isMD;

  const navGroups = [
    {
      title: 'Main',
      items: [
        ...(!isOrgAdmin ? [
          { 
            name: isManagerNotMD ? 'Team Dashboard' : 'Dashboard', 
            to: isManagerNotMD ? '/dashboard/team' : (isMD ? '/dashboard/md' : '/dashboard/employee'), 
            icon: LayoutDashboard,
            end: true
          },
          ...(isManagerNotMD ? [{ 
            name: 'My Dashboard', 
            to: '/dashboard/employee', 
            icon: UserIcon,
            end: true
          }] : [])
        ] : []),
        ...(!isOrgAdmin && !isMD ? [{ name: 'My Leaves', to: '/leaves/my', icon: CalendarDays, id: 'tour-nav-my-leaves' }] : []),
        ...(!isOrgAdmin && !isMD ? [{ name: 'Apply Leave', to: '/leaves/apply', icon: FilePlus, id: 'tour-apply' }] : []),
        { name: 'Team Calendar', to: '/approvals/team-calendar', icon: Users, id: 'tour-nav-team' },
        { name: 'Policies', to: '/admin/policies', icon: ShieldCheck },
      ],
    },
    ...(isManagerPlus ? [{
      title: 'Approvals',
      items: [
        { name: 'Inbox', to: '/approvals/inbox', icon: Inbox, id: 'tour-nav-inbox' },
        { name: 'Reports', to: '/admin/reports', icon: FileText },
      ],
    }] : []),
    ...(isOrgAdmin || isHR ? [{
      title: 'Administration',
      items: [
        ...(isOrgAdmin ? [
          { name: 'Users', to: '/admin/users', icon: UserCog },
          { name: 'Settings', to: '/admin/settings', icon: Settings, id: 'tour-admin-settings' },
        ] : []),
        { name: 'Leave Types', to: '/admin/leave-types', icon: Flag },
        { name: 'Policies', to: '/admin/policies', icon: ShieldCheck },
        { name: 'Holidays', to: '/admin/holidays', icon: Coffee },
        { name: 'Reports', to: '/admin/reports', icon: FileText },
        { name: 'Audit Log', to: '/admin/audit', icon: ShieldCheck },
      ],
    }] : []),
  ];

  const getPageDetails = () => {
    const path = location.pathname;

    if (path.includes('/dashboard/employee')) {
      return {
        title: 'Employee Dashboard',
        description: 'Overview of your leave balances, request status, and upcoming holidays'
      };
    }
    if (path.includes('/dashboard/md')) {
      return {
        title: 'Executive Dashboard',
        description: 'Strategic overview of organization-wide leave patterns, trends, and approvals'
      };
    }
    if (path.includes('/dashboard/team')) {
      return {
        title: 'Team Dashboard',
        description: "Monitor your direct reports' leave history, calendars, and pending requests"
      };
    }
    if (path.includes('/dashboard')) {
      const roleName = user.role === 'MD' ? 'Executive' : (isManagerNotMD ? 'Team' : 'Employee');
      return {
        title: `${roleName} Dashboard`,
        description: 'Overview of leave balances, requests, and activity'
      };
    }
    if (path.includes('/leaves/apply')) {
      return {
        title: 'Apply for Leave',
        description: 'Submit a new leave request for manager approval'
      };
    }
    if (path.includes('/leaves/my')) {
      return {
        title: 'My Leaves',
        description: 'Track the status of your submitted leave applications and history'
      };
    }
    if (path.includes('/approvals/inbox')) {
      return {
        title: 'Approval Inbox',
        description: 'Review, approve, or reject leave requests submitted by your team'
      };
    }
    if (path.includes('/approvals/team-calendar')) {
      return {
        title: 'Team Calendar',
        description: 'Visual timeline of scheduled absences across all teams'
      };
    }
    if (path.includes('/admin/hierarchy')) {
      return {
        title: 'Hierarchy Management',
        description: 'Manage employee reporting structures and approval lines'
      };
    }
    if (path.includes('/admin/users')) {
      return {
        title: 'User Management',
        description: 'Manage employee profiles, roles, departments, and system access'
      };
    }
    if (path.includes('/admin/leave-types')) {
      return {
        title: 'Leave Types',
        description: 'Configure leave categories, bounds, and general availability'
      };
    }
    if (path.includes('/admin/policies')) {
      return {
        title: 'Leave Policies',
        description: 'Define rules, accrual frequencies, and carry-forward settings'
      };
    }
    if (path.includes('/admin/holidays')) {
      return {
        title: 'Holidays',
        description: 'Maintain the company calendar with official public holidays'
      };
    }
    if (path.includes('/admin/settings')) {
      return {
        title: 'System Settings',
        description: 'Configure organization-wide defaults, security, and preferences'
      };
    }
    if (path.includes('/admin/reports')) {
      return {
        title: 'Reports & Analytics',
        description: 'Generate reports on leave utilization, department trends, and metrics'
      };
    }
    if (path.includes('/admin/audit')) {
      return {
        title: 'Audit Log',
        description: 'Trace system events, configuration changes, and administrative actions'
      };
    }
    if (path.includes('/profile')) {
      return {
        title: 'My Profile',
        description: 'Update your personal details, designation, contact info, and preferences'
      };
    }
    return {
      title: 'NeuralNet LMS',
      description: 'NeuralNet Leave Management System'
    };
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-[#0f172a] text-slate-300">
      <div className="h-20 flex items-center px-6 border-b border-slate-800">
        <img src="/logos/Neuralnet_logo.png" alt="NeuralNet Logo" className="h-8 w-auto" />
      </div>

      <div className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
        {navGroups.map((group, i) => (
          <div key={i}>
            <h3 className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              {group.title}
            </h3>
            <nav className="space-y-1">
              {group.items.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.to}
                  id={item.id}
                  end={item.end}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `sidebar-link ${isActive ? 'active' : ''}`
                  }
                >
                  <item.icon className="w-5 h-5 shrink-0" />
                  {item.name}
                </NavLink>
              ))}
            </nav>
          </div>
        ))}
      </div>

      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center gap-3 mb-4">
          <Avatar name={user.name} photoUrl={user.photoUrl} size="md" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user.name}</p>
            <p className="text-xs text-slate-400 truncate">{user.designation || user.role}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }}
            className="flex-1 flex justify-center items-center gap-2 py-2 text-xs font-medium bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <Settings className="w-3.5 h-3.5" /> Profile
          </button>
          <button
            onClick={handleLogout}
            className="flex-1 flex justify-center items-center gap-2 py-2 text-xs font-medium text-red-400 hover:text-red-300 bg-red-950/30 hover:bg-red-900/30 rounded-lg transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>
      </div>
    </div>
  );

  const pageDetails = getPageDetails();

  return (
    <div className="h-screen w-screen overflow-hidden flex" style={{ backgroundColor: 'var(--page-bg, #f8fafc)' }}>
      {/* Mobile sidebar backdrop */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/80 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-auto ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        <SidebarContent />
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Navbar */}
        <header className="h-20 glass sticky top-0 z-30 border-b border-slate-200/60 dark:border-slate-800/60 px-4 sm:px-6 lg:px-8 grid grid-cols-3 items-center relative">
          {/* Top Gradient Accent Bar */}
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"></div>

          {/* Left Column: Mobile Menu button */}
          <div className="flex items-center justify-start">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2.5 -ml-1 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100/80 dark:hover:bg-slate-800 rounded-xl transition-colors shrink-0"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>

          {/* Center Column: Page Title & Description */}
          <div className="flex flex-col items-center justify-center min-w-0 py-1">
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight leading-none bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent dark:from-blue-400 dark:to-indigo-400 text-center truncate w-full">
              {pageDetails.title}
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-semibold leading-normal mt-1.5 text-center truncate w-full hidden sm:block">
              {pageDetails.description}
            </p>
          </div>

          {/* Right Column: Actions */}
          <div className="flex items-center gap-1.5 justify-end shrink-0">
            {/* Theme Toggle */}
            <button
              onClick={cycleTheme}
              title={`Theme: ${theme}`}
              className="p-2.5 text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50/80 dark:hover:bg-slate-800 rounded-xl transition-all duration-200"
            >
              <ThemeIcon className="w-5 h-5" />
            </button>
            {/* Notification Bell */}
            <button
              id="tour-notifications"
              onClick={togglePanel}
              className="relative p-2.5 text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50/80 dark:hover:bg-slate-800 rounded-xl transition-all duration-200"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse-soft border-2 border-white dark:border-slate-800"></span>
              )}
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="mx-auto max-w-7xl animate-fade-in">
            {(!user.designation || user.designation.trim() === '') && (
              <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg shadow-sm flex items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-sm font-bold text-blue-800">Complete Your Profile</h3>
                  <p className="text-sm text-blue-700 mt-0.5">Please update your Designation in your profile settings to keep your record complete.</p>
                </div>
                <button
                  onClick={() => navigate('/profile')}
                  className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold py-2 px-4 rounded-lg transition-colors shadow-sm"
                >
                  Update Now
                </button>
              </div>
            )}
            <Outlet />
          </div>
        </main>
      </div>

      <NotificationPanel />
    </div>
  );
}
